import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/contexts/LangContext";
import PageMeta from "@/components/PageMeta";
import { ArrowUpRight, Building2, Award, Globe2 } from "lucide-react";

const TEAM_BY_LANG = {
  de: [
    { name: "Johannes Fernberg", role: "CEO Royalhouse Bauträger", bio: "Vorstand RSH · ECO Building Technik · Royal Living Handelsgesellschaft.", image: "/images/johannes-fernberg.jpg" },
  ],
  en: [
    { name: "Johannes Fernberg", role: "CEO Royalhouse Bauträger", bio: "Board RSH · ECO Building Technik · Royal Living Handelsgesellschaft.", image: "/images/johannes-fernberg.jpg" },
  ],
  ru: [
    { name: "Johannes Fernberg", role: "CEO Royalhouse Bauträger", bio: "Правление RSH · ECO Building Technik · Royal Living.", image: "/images/johannes-fernberg.jpg" },
  ],
};

const PARTNER_FIRMS_BY_LANG = {
  de: {
    heading: "Partnerunternehmen",
    firms: [
      { name: "Budova", sub: "Industrieller Backbone", href: "/partner/budova" },
      { name: "Royal House Kyiw", sub: "Fertigteilbau Ukraine", href: "/partner/royalhouse-ua" },
      { name: "Well-Being ConTech", sub: "Bautechnologie & Smart Home", href: "/partner/wellbeing" },
      { name: "ECO Building Technik", sub: "Haustechnik & Lieferant", href: "/partner/building-technik" },
      { name: "Ukraine Rebuilding Alliance", sub: "Wiederaufbau-Initiative", href: "/partner/ukraine-rebuild" },
    ],
  },
  en: {
    heading: "Partner companies",
    firms: [
      { name: "Budova", sub: "Industrial backbone", href: "/partner/budova" },
      { name: "Royal House Kyiv", sub: "Prefab construction Ukraine", href: "/partner/royalhouse-ua" },
      { name: "Well-Being ConTech", sub: "Construction tech & smart home", href: "/partner/wellbeing" },
      { name: "ECO Building Technik", sub: "Building services & supplier", href: "/partner/building-technik" },
      { name: "Ukraine Rebuilding Alliance", sub: "Reconstruction initiative", href: "/partner/ukraine-rebuild" },
    ],
  },
  ru: {
    heading: "Компании-партнёры",
    firms: [
      { name: "Budova", sub: "Индустриальная основа", href: "/partner/budova" },
      { name: "Royal House Киев", sub: "Префаб-строительство", href: "/partner/royalhouse-ua" },
      { name: "Well-Being ConTech", sub: "Стройтехнологии и умный дом", href: "/partner/wellbeing" },
      { name: "ECO Building Technik", sub: "Инженерные системы и поставки", href: "/partner/building-technik" },
      { name: "Ukraine Rebuilding Alliance", sub: "Инициатива восстановления", href: "/partner/ukraine-rebuild" },
    ],
  },
};

const ARCHITECTS_BY_LANG = {
  de: [
    { name: "Oleg Drozdov", title: "Architekt · Drozdov & Partners" },
    { name: "Julian Chaplinsky", title: "Architekt · Stadtentwicklung" },
    { name: "Dmytro Vasyljev", title: "Architekt · Wohnbau" },
    { name: "Paolo Stabellini", title: "Architekt · Internationale Projekte" },
  ],
  en: [
    { name: "Oleg Drozdov", title: "Architect · Drozdov & Partners" },
    { name: "Julian Chaplinsky", title: "Architect · Urban development" },
    { name: "Dmytro Vasyljev", title: "Architect · Residential" },
    { name: "Paolo Stabellini", title: "Architect · International projects" },
  ],
  ru: [
    { name: "Oleg Drozdov", title: "Архитектор · Drozdov & Partners" },
    { name: "Julian Chaplinsky", title: "Архитектор · Градостроительство" },
    { name: "Dmytro Vasyljev", title: "Архитектор · Жильё" },
    { name: "Paolo Stabellini", title: "Архитектор · Международные проекты" },
  ],
};

const PARTNERS = [
  "Well-Being ConTech",
  "Budova",
  "ECO Building Technik",
  "Ukraine Rebuilding Alliance",
  "Business4All",
  "Haier Smart Home",
  "Zigbee Alliance",
  "BIM Standards",
  "Royal Living",
];

const VALUES_BY_LANG = {
  de: [
    { icon: Building2, title: "Bauen als Ökosystem", text: "Von Idee bis Betrieb — ein durchgängiger digitaler Prozess. Kein Bruch, keine Übersetzungsverluste." },
    { icon: Award, title: "Industrielle Qualität", text: "Werkseitig produziert, geprüft und freigegeben. Qualitätssicherung wo sie hingehört: ans Bauteil." },
    { icon: Globe2, title: "Internationale Allianz", text: "Top-Architekten, BIM-Spezialisten, Smart-Home-Partner — unser Netzwerk liefert Best-in-Class." },
  ],
  en: [
    { icon: Building2, title: "Building as ecosystem", text: "From idea to operations — one continuous digital process. No breaks, no translation losses." },
    { icon: Award, title: "Industrial quality", text: "Manufactured, inspected and released in the factory. Quality assurance where it belongs: at the component." },
    { icon: Globe2, title: "International alliance", text: "Top architects, BIM specialists, smart-home partners — our network delivers best-in-class." },
  ],
  ru: [
    { icon: Building2, title: "Строительство как экосистема", text: "От идеи до эксплуатации — единый цифровой процесс. Без разрывов и потерь." },
    { icon: Award, title: "Индустриальное качество", text: "Изготовлено, проверено и выпущено на заводе. Контроль качества — там, где он нужен: на детали." },
    { icon: Globe2, title: "Международный альянс", text: "Топ-архитекторы, BIM-специалисты, партнёры по умному дому — лучшее в классе." },
  ],
};

export default function UeberUnsPage() {
  const { t, lang } = useLang();
  const TEAM = TEAM_BY_LANG[lang] || TEAM_BY_LANG.ru || TEAM_BY_LANG.de;
  const VALUES = VALUES_BY_LANG[lang] || VALUES_BY_LANG.ru || VALUES_BY_LANG.de;
  const ARCHITECTS = ARCHITECTS_BY_LANG[lang] || ARCHITECTS_BY_LANG.ru || ARCHITECTS_BY_LANG.de;
  const PARTNER_FIRMS = PARTNER_FIRMS_BY_LANG[lang] || PARTNER_FIRMS_BY_LANG.de;
  return (
    <div data-testid="ueber-uns-page" className="pt-32">
      <PageMeta pageKey="about" />
      {/* Header */}
      <section className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16 pb-16">
        <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-6">{t("about.eyebrow")}</p>
        <h1 className="font-display text-5xl md:text-7xl lg:text-8xl font-light uppercase tracking-tighter leading-[0.95]">
          {t("about.title.l1")}<br />
          {t("about.title.l2")} <span className="italic">{t("about.title.l2_em")}</span>
        </h1>
        <p className="mt-10 max-w-3xl text-lg md:text-xl text-white/70 font-light leading-relaxed">
          {t("about.lede")}
        </p>
      </section>

      {/* Image + values */}
      <section className="grid md:grid-cols-12 border-y border-white/10">
        <div className="md:col-span-6 aspect-[4/3] md:aspect-auto overflow-hidden border-b md:border-b-0 md:border-r border-white/10">
          <img
            src="/images/about-us-bautrager-hero.jpg"
            alt="Royalhouse Bauträger Projekt"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="md:col-span-6 p-8 md:p-16 lg:p-20 flex flex-col justify-center">
          <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-8">{t("about.values.eyebrow")}</p>
          <div className="space-y-10">
            {VALUES.map((v) => (
              <div key={v.title} className="flex gap-6">
                <div className="shrink-0 w-12 h-12 border border-white/20 flex items-center justify-center">
                  <v.icon size={20} strokeWidth={1.25} />
                </div>
                <div>
                  <h3 className="font-display text-xl md:text-2xl font-medium tracking-tight mb-2">
                    {v.title}
                  </h3>
                  <p className="text-sm md:text-base text-white/70 leading-relaxed">{v.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Geschäftsleitung */}
      <section className="py-24 md:py-32 max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16">
        <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-6">{t("about.team.eyebrow")}</p>
        <h2 className="font-display text-3xl md:text-5xl font-light uppercase tracking-tight leading-[1.05] mb-16">
          {t("about.team.title.l1")} <br />{t("about.team.title.l2")}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-stretch">
          {TEAM.map((m) => (
            <div
              key={m.name}
              className="border border-white/10 hover:border-white/30 transition-colors group overflow-hidden"
              data-testid={`team-${m.name.replace(/\s+/g, "-").toLowerCase()}`}
            >
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src={m.image}
                  alt={m.name}
                  className="w-full h-full object-cover object-top grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                />
              </div>
              <div className="p-8">
                <h3 className="font-display text-2xl md:text-3xl font-medium tracking-tight">{m.name}</h3>
                <p className="text-[11px] tracking-[0.25em] uppercase text-white/50 mt-2">{m.role}</p>
                <p className="mt-4 text-sm text-white/65 leading-relaxed">{m.bio}</p>
              </div>
            </div>
          ))}

          {/* Partner companies (linked) */}
          <div className="border border-white/10 flex flex-col" data-testid="about-partner-firms">
            <div className="p-8 border-b border-white/10">
              <p className="text-[11px] tracking-[0.3em] uppercase text-white/50">{PARTNER_FIRMS.heading}</p>
            </div>
            <div className="flex-1 flex flex-col divide-y divide-white/10">
              {PARTNER_FIRMS.firms.map((f) => (
                <Link
                  key={f.name}
                  to={f.href}
                  data-testid={`partner-firm-${f.name.replace(/\s+/g, "-").toLowerCase()}`}
                  className="group flex items-center justify-between gap-6 px-8 py-6 flex-1 hover:bg-white/[0.04] transition-colors"
                >
                  <div>
                    <h3 className="font-display text-xl md:text-2xl font-medium tracking-tight group-hover:text-white transition-colors">
                      {f.name}
                    </h3>
                    <p className="text-[11px] tracking-[0.25em] uppercase text-white/50 mt-1.5">{f.sub}</p>
                  </div>
                  <span className="shrink-0 w-10 h-10 border border-white/20 flex items-center justify-center group-hover:bg-white group-hover:text-[#051630] group-hover:border-white transition-all">
                    <ArrowUpRight size={16} strokeWidth={1.5} />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Architects */}
      <section className="bg-[#0A2244]/50 border-y border-white/10 py-24 md:py-32">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16">
          <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-6">
            {t("about.architects.eyebrow")}
          </p>
          <h2 className="font-display text-3xl md:text-5xl font-light uppercase tracking-tight leading-[1.05] mb-16 max-w-3xl">
            {t("about.architects.title.l1")} <br />{t("about.architects.title.l2")}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-white/10">
            {ARCHITECTS.map((a, i) => (
              <div key={a.name} className="border-b border-r border-white/10 p-8 md:p-10">
                <span className="font-display text-2xl text-white/30">0{i + 1}</span>
                <h3 className="font-display text-xl md:text-2xl font-medium tracking-tight mt-6">
                  {a.name}
                </h3>
                <p className="text-[11px] tracking-[0.25em] uppercase text-white/50 mt-3">
                  {a.title}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners marquee */}
      <section className="py-20 md:py-28 overflow-hidden">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16 mb-12">
          <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-6">{t("about.partners.eyebrow")}</p>
          <h2 className="font-display text-3xl md:text-5xl font-light uppercase tracking-tight leading-[1.05]">
            {t("about.partners.title")}
          </h2>
        </div>
        <div className="relative">
          <div className="flex w-max rh-marquee">
            {[...PARTNERS, ...PARTNERS].map((p, i) => (
              <div
                key={`${p}-${i < PARTNERS.length ? "a" : "b"}`}
                className="px-10 md:px-16 py-8 border-r border-white/10 font-display text-xl md:text-2xl tracking-tight text-white/60 whitespace-nowrap"
              >
                {p}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 border-t border-white/10 bg-[#051630] relative overflow-hidden">
        <div className="absolute inset-0 rh-grid-bg opacity-40" />
        <div className="relative max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16 text-center">
          <h2 className="font-display text-4xl md:text-6xl font-light uppercase tracking-tight leading-[1.05] max-w-4xl mx-auto">
            {t("about.cta.title.l1")} <br />{t("about.cta.title.l2")} <span className="italic">{t("about.cta.title.l2_em")}</span>
          </h2>
          <Link
            to="/kontakt"
            data-testid="ueber-uns-cta"
            className="mt-12 rh-btn-primary inline-flex items-center justify-center gap-3 bg-white text-[#051630] font-bold tracking-[0.18em] uppercase text-xs px-8 py-5 border border-white"
          >
            <span>{t("nav.cta")}</span>
            <ArrowUpRight size={16} strokeWidth={1.5} />
          </Link>
        </div>
      </section>
    </div>
  );
}
